import { readTmyHottestHoursSummary, tmyHottestHoursSource } from '../economicContribution/tmyCoincidentWetBulb';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { DataSource } from '../../types/siteRag';
import type { SourceRoleTruthFinding } from './sourceRoleTruthGuard';
import { NUMBERED_REF_TOKEN_RE } from '../citationTokens';

type WaterClimateSed = Partial<Pick<SiteEvidenceDocument, 'waterResource' | 'climate' | 'economicEvidence' | 'location'>>;

function citedReferences(sentence: string, references: readonly DataSource[]): DataSource[] {
  // Scan the same complete grammar as source-role checking and reference
  // renumbering. Reliability labels and colon/compound spellings do not
  // change what the frozen cited source can establish. Bare lists, ranges and
  // malformed tokens remain non-citations; use a fresh cursor for every scan.
  return [...sentence.matchAll(new RegExp(NUMBERED_REF_TOKEN_RE.source, NUMBERED_REF_TOKEN_RE.flags))]
    .flatMap(match => (match[0].match(/\d+/g) ?? []).map(index => references[Number(index) - 1]))
    .filter((reference): reference is DataSource => !!reference);
}

function claimReferences(clause: string, sentence: string, references: readonly DataSource[]): DataSource[] {
  const tail = sentence.slice(sentence.indexOf(clause) + clause.length);
  // A same-subject qualifier can carry the citation for the preceding identity
  // ("..., but its local yield is unknown [Ref]"). A peer/other assertion may
  // not lend either its provider citation or its independent study to this one.
  const qualifier = /^\s*,?\s*but\s+its\b/i.test(tail) ? tail.split(';')[0]! : '';
  return citedReferences(clause + qualifier, references);
}

/** This guard establishes only what the exact cited provider can support.
 * A separately cited site investigation remains for document grounding. */
function hasSiteStudy(references: readonly DataSource[], kind: 'aquifer' | 'climate'): boolean {
  return references.some(reference => {
    if (reference.demonstrationDisclosure) return false;
    const metadata = `${reference.field} ${reference.source}`;
    if (/\b(?:generic|regional|historical|AI|research|overview|atlas)\b/i.test(metadata)) return false;
    const document = !!reference.documentId || reference.url?.startsWith('doc://');
    const primary = reference.qualification === 'verified'
      || reference.sourceGrade === 'verified-professional' || /\bsite[- ]specific\b/i.test(metadata);
    const domain = kind === 'aquifer'
      ? /\b(?:aquifer|hydrogeolog\w*|groundwater)\b/i.test(metadata)
      : /\b(?:climate|weather|thermal|cooling|mechanical)\b/i.test(metadata);
    return !!document && primary && domain && /\b(?:study|report|assessment|investigation|design[- ]basis)\b/i.test(metadata);
  });
}

function citesProvider(references: readonly DataSource[], field: 'waterResource' | 'climate',
  source: string | undefined, url: string | undefined): boolean {
  return references.some(reference => reference.field === field && !reference.documentId
    && !reference.url?.startsWith('doc://')
    && ((!!source && reference.source === source) || (!!url && reference.url === url)));
}

function isQualifiedClaim(clause: string, assertion: string, start: number): boolean {
  const prefix = clause.slice(0, start);
  return /\?(?:\s*\[Ref[^\]]+\])*\s*$/.test(clause)
    || /\b(?:if|whether)\b/i.test(prefix)
    || /\b(?:may|might|could|unconfirmed|unverified|unknown|unavailable|hypothetical|assumed|n\/a|not\s+(?:confirmed|established|known|available|mapped|a\s+design))\b/i.test(assertion)
    || /\b(?:not\s+true\s+that|no\s+evidence\s+that|does\s+not\s+(?:establish|confirm|mean)\s+that|cannot\s+(?:establish|confirm)|not|hypothetical|assumed)\b[^.;]{0,35}$/i.test(prefix)
    || /\b(?:AI|research|developer|owner)\b[^.;]{0,70}\b(?:suggests?|reports?|proposes?|assumes?|names?)\b/i.test(prefix)
    || /\b(?:peer|another|other|comparison)\s+(?:site|project|facility)\b/i.test(clause)
      && !/\b(?:this|our|subject)\s+(?:site|project|facility)\b/i.test(clause);
}

interface ScreeningWetBulb { value: number; expected: string; sourcePaths: string[]; month?: RegExp; tmy?: { field: string; url: string } }

/** Full-name suffixes for NOAA month labels, e.g. Jan → Jan|January. */
const MONTH_SUFFIX: Readonly<Record<string, string>> = {
  Jan: 'uary', Feb: 'ruary', Mar: 'ch', Apr: 'il', May: '', Jun: 'e',
  Jul: 'y', Aug: 'ust', Sep: 't(?:ember)?', Oct: 'ober', Nov: 'ember', Dec: 'ember',
};

const finiteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

/** Wet-bulb values the climate provider carries only as screening statistics.
 * The Open-Meteo entry comes first so a value shared with NOAA keeps its
 * original finding. NOAA `designWetBulb` / `peakDesignWetBulb` are legacy names
 * for Stull estimates at an assumed RH (see noaaNormalsBasis.ts). */
function screeningWetBulbs(sed: WaterClimateSed): ScreeningWetBulb[] {
  const climate = sed.climate;
  const data = climate?.data;
  const values: ScreeningWetBulb[] = [];
  if (finiteNumber(data?.peakWetBulb) && /historical/i.test(climate?.source ?? '')) {
    values.push({
      value: data.peakWetBulb,
      expected: 'The cited historical climate value is a wet-bulb screening statistic, not an equipment design condition. Preserve its historical/peak-month basis; a design percentile and equipment duty require separate design evidence.',
      sourcePaths: ['climate.source', 'climate.data.peakWetBulb', 'climate.data.monthlySummary'],
    });
  }
  const paired = sed.location ? readTmyHottestHoursSummary(data?.tmyHottestHours, {
    id: sed.economicEvidence?.data?.waterUseScreening?.siteId, location: sed.location,
  }) : null;
  if (paired) values.push({ value: paired.meanWetBulbC,
    expected: 'The cited paired mean wet bulb of the hottest 35 TMY hours is a typical-year screening statistic, not an ASHRAE design percentile or equipment duty. Combining it with the SED peak dry bulb is noncoincident and may overstate adiabatic headroom; retain the dataset/coordinate basis and require separate design evidence.',
    sourcePaths: ['climate.data.tmyHottestHours', 'climate.data.tmyHottestHours.summaryHash', 'climate.data.tmyHottestHours.datasetHash'],
    tmy: { field: `economicEvidence.waterUseScreening.tmy-hottest-hours-${paired.summaryHash}`, url: tmyHottestHoursSource(paired).url },
  });
  const noaa = /historical|normals/i.test(climate?.source ?? '') ? data?.noaaNormals : undefined;
  if (!noaa) return values;
  const expected = 'The cited NOAA normals wet bulb is a screening estimate from monthly mean temperature at an assumed relative humidity, not an ASHRAE design percentile or equipment design condition. Preserve its estimate basis; a design percentile and equipment duty require separate design evidence.';
  const sourcePaths = ['climate.source', 'climate.data.noaaNormals.peakDesignWetBulb', 'climate.data.noaaNormals.monthly'];
  const peak = noaa.peakDesignWetBulb;
  if (finiteNumber(peak)) values.push({ value: peak, expected, sourcePaths });
  // A non-peak monthly value counts only when its month is named: winter
  // estimates overlap cooling-tower approach/range deltas (3–8°C). Month names
  // match case-sensitively so the modal "may" is not the month May. Accepted
  // miss: an unnamed-month claim such as "winter design wet bulb of 5.1°C".
  for (const { month, designWetBulb } of noaa.monthly ?? []) {
    if (!finiteNumber(designWetBulb) || designWetBulb === peak) continue;
    values.push({ value: designWetBulb, expected, sourcePaths,
      month: new RegExp(`\\b${month}(?:${MONTH_SUFFIX[month] ?? ''})?\\b`) });
  }
  return values;
}

/** Named aquifers and equipment design weather cannot acquire primary-source
 * authority merely by citing a provider whose frozen result lacks that scope.
 * This is deliberately not an absence-based rejection of independent studies. */
export function waterClimateSourceScopeFindings(
  text: string,
  sed: WaterClimateSed,
  sectionId: string,
  references: readonly DataSource[] = [],
): SourceRoleTruthFinding[] {
  const findings: SourceRoleTruthFinding[] = [];
  const add = (excerpt: string, expected: string, sourcePaths: string[]) => findings.push({
    kind: 'source-document-citation', severity: 'block', sectionId,
    excerpt, observed: excerpt, expected, message: expected, sourcePaths,
  } satisfies SourceRoleTruthFinding);
  for (const sentence of text.split(/(?<=[.!?])(?<!\bU\.S\.)\s+|\n+/)) {
    if (!citedReferences(sentence, references).length) continue;
    const clauses = sentence.split(/;\s*|,?\s+(?:but|whereas|however)\s+/i);
    const water = sed.waterResource;
    if (water?.data && water.data.principalAquifer == null) {
      for (const clause of clauses) {
        const cited = claimReferences(clause, sentence, references);
        if (!citesProvider(cited, 'waterResource', water.source, water.sourceUrl) || hasSiteStudy(cited, 'aquifer')) continue;
        const named = /\b(?:site\s+(?:is\s+(?:located|situated)\s+(?:over|above)|overlies|is\s+underlain\s+by)|(?:underlying|principal)\s+aquifer\s+is)\s+(?:the\s+)?[^.;,]{2,100}\baquifer\b/i.exec(clause);
        if (!named || isQualifiedClaim(clause, named[0], named.index)) continue;
        add(clause,
          'The cited water-resource provider does not establish the site aquifer identity. State that identity is unconfirmed; any AI-research suggestion must retain its research attribution and requires a site-specific hydrogeological source.',
          ['waterResource.data.principalAquifer', 'waterResource.source', 'waterResource.sourceUrl']);
      }
    }
    const climate = sed.climate;
    const wetBulbs = screeningWetBulbs(sed);
    if (wetBulbs.length === 0) continue;
    for (const clause of clauses) {
      const cited = claimReferences(clause, sentence, references);
      if (hasSiteStudy(cited, 'climate')) continue;
      if (!/\bwet[- ]bulb\b/i.test(clause)) continue;
      const temperatures = [...clause.matchAll(/(?<![\w.])([+-]?\d+(?:\.\d+)?)\s*°?\s*C\b/gi)];
      for (const match of temperatures) {
        const wetBulb = wetBulbs.find(candidate => candidate.value === Number(match[1])
          && (!candidate.month || candidate.month.test(clause))
          && (candidate.tmy ? cited.some(ref => ref.field === candidate.tmy!.field && ref.url === candidate.tmy!.url
            && !ref.documentId && !ref.url?.startsWith('doc://'))
            : citesProvider(cited, 'climate', climate?.source, climate?.sourceUrl)));
        if (!wetBulb) continue;
        const before = clause.slice(0, match.index).slice(-90);
        const after = clause.slice(match.index! + match[0].length, match.index! + match[0].length + 90);
        // A temperature difference ("5.1°C approach", "5.1°C design wet-bulb
        // range") is not a wet bulb, even when it equals a stored estimate.
        if (/^\s*(?:(?:design|wet[- ]bulb)[\s-]+){0,2}(?:approach|range|depression|delta|difference|margin|allowance)\b/i.test(after)) continue;
        // Look through a closed parenthetical with no temperature of its own
        // ("(30-yr normal)", "(2021-2025)"), whose digits would otherwise end
        // the look-back. One carrying a temperature ("(26.1°C)", "(79°F)")
        // still binds "design" to that value. Same-length masking keeps the
        // offsets valid, and the assertion keeps the original wording so a
        // qualifier inside the parenthetical still counts.
        const lookBack = before.replace(/\([^()]*\)/g, group => /\d\s*°?\s*[CF]\b/i.test(group) ? group : ' '.repeat(group.length));
        const precedingDesign = /\bdesign(?:[- ](?:peak|basis|wet[- ]bulb))?\b[^.;\d]{0,55}$/i.exec(lookBack);
        // "is/as the design wet bulb", or adjectival "24.5°C design wet bulb" /
        // "24.5°C wet-bulb design basis".
        const followingDesign = /^\s*(?:(?:is|as)\s+(?:the\s+)?)?(?:design[\s-]+(?:peak|basis|wet[- ]bulb)|wet[- ]bulb[\s-]+design)\b/i.exec(after);
        const assertion = precedingDesign ? before.slice(precedingDesign.index) + match[0] : followingDesign ? match[0] + followingDesign[0] : null;
        const start = precedingDesign ? match.index! - before.length + precedingDesign.index : match.index!;
        if (!assertion || isQualifiedClaim(clause, assertion, start)) continue;
        add(clause, wetBulb.expected, wetBulb.sourcePaths);
        break;
      }
    }
  }
  return findings;
}
